import { useCallback, useEffect, useRef, useState } from "react";

import { fetchBlogs, type BlogListResult } from "@/api/blogs";

type UseBlogsOptions = {
  page?: number;
  limit?: number;
  status?: "published" | "draft" | "all";
  tag?: string;
  /**
   * Résultat déjà connu au premier rendu, fourni par le rendu serveur.
   *
   * Évite que la liste disparaisse entre la peinture initiale et l'hydratation.
   * À ne fournir que si les filtres correspondent à ceux du rendu serveur.
   */
  initialData?: BlogListResult | null;
};

export function useBlogs(options?: UseBlogsOptions) {
  const initialData = options?.initialData ?? null;
  // Seul le tout premier passage de l'effet épargne l'état initial ; tout
  // changement de filtre ensuite repart d'une liste vide, comme auparavant.
  const hasSeededRef = useRef(Boolean(initialData));
  const [data, setData] = useState<BlogListResult | null>(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 9;
  const status = options?.status;
  const tag = options?.tag;

  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (hasSeededRef.current) {
      hasSeededRef.current = false;
    } else {
      setLoading(true);
      setData(null);
    }

    fetchBlogs({ page, limit, status, tag })
      .then((blogs) => {
        if (!isMounted) return;
        setData(blogs);
        setError(null);
      })
      .catch(() => {
        if (!isMounted) return;
        setData(null);
        setError("Impossible de charger les articles.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [limit, page, refreshKey, status, tag]);

  return { data, loading, error, refetch };
}
